// import { useDidShow, useRouter } from '@tarojs/taro'
import { useSetState } from 'ahooks'
import { cloneDeep, isEmpty, merge } from 'lodash-es'
import { useLocalObservable } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

import { type UnifiedOptionStoreType, type UnifiedStateStoreType, usePageStore } from '@/hooks'
import useVariable from '@/hooks/useVariable'
import { unifiedOptionStoreNoContext } from '@/store/unified/unifiedOptionStore'
import { unifiedStoreNoContext } from '@/store/unified/unifiedStore'
import service from '@/utils/service'

import { StatusCode } from '../enum'

import usePcTransform from './usePcTransform'

export { usePcTransform }

// 获取通用表单
export const getCommonForm = (data: any, url?: any): Promise<any> =>
  service<any>({ method: 'get', url: `/common/form_json${url}`, data })

/**
 * 动态获取通用表单
 * @param code 表单code
 * @param qr_code_id 免登录扫码id
 */
export default ({
  code,
  vars,
  stateStore: propStateStore,
  optionStore: propOptionStore,
  immediate,
  ignoreStore,
  isPreview,
  isLocal,
  localOption,
  didmount
}: {
  code: string
  vars?: any
  stateStore?: UnifiedStateStoreType
  optionStore?: UnifiedOptionStoreType
  immediate?: boolean
  ignoreStore?: boolean
  isPreview?: boolean
  isLocal?: boolean
  localOption?: any
  didmount?: Function
}) => {
  const _optionStore = useLocalObservable(() => unifiedOptionStoreNoContext)
  const _stateStore = useLocalObservable(() => unifiedStoreNoContext)
  const stateStore = propStateStore || _stateStore
  const optionStore = propOptionStore || _optionStore
  const params = useParams()
  const [searchParams] = useSearchParams()
  const [state, setState] = useSetState<{
    option: any
    originOption: any
    optionStatus: StatusCode
    variables?: any
  }>({
    option: undefined,
    originOption: undefined,
    optionStatus: StatusCode.pending
  })
  const { transform } = usePcTransform()
  const stateRef = useRef<any>(state)
  stateRef.current = state
  const setFormOptionItem = (formInfo: any, items: { [key: string]: any }, extraName?: string) => {
    if (extraName) {
      // TODO
    } else {
      const info = cloneDeep(formInfo)
      Object.entries(items).forEach(([key, value]) => {
        const columns = key.split('.')
        if (columns?.length === 2) {
          const blockIndex = info.findIndex(
            (item: any) => item.id === columns[0] || item.key === columns[0]
          )
          const itemIndex = info?.[blockIndex]?.form_list?.findIndex(
            (item: any) => item.name === columns[1] || item.formItemProps?.name === columns[1]
          )
          if (blockIndex > -1 && itemIndex > -1) {
            info[blockIndex].form_list[itemIndex] = merge(
              info[blockIndex].form_list[itemIndex],
              value
            )
          } else {
            throw new Error(`设置表单项找不到 ${key}`)
          }
        }
      })
      if (stateRef.current.option?.formInfo) {
        setState({ option: merge(stateRef.current.option, { formInfo: info }) })
      }
      return info
    }
  }
  const getFormOptionItem = (formInfo: any, key: string, extraName?: string) => {
    if (extraName) {
      // TODO
    } else {
      const info = formInfo
      const columns = key.split('.')
      if (columns?.length === 2) {
        const blockIndex = info.findIndex(
          (item: any) => item.id === columns[0] || item.key === columns[0]
        )
        const itemIndex = info?.[blockIndex]?.form_list?.findIndex(
          (item: any) => item.name === columns[1] || item.formItemProps?.name === columns[1]
        )
        if (blockIndex > -1 && itemIndex > -1) {
          return info[blockIndex].form_list[itemIndex]
        } else {
          throw new Error(`设置表单项找不到 ${key}`)
        }
      }
      return {}
    }
  }
  const injectVariable = useVariable({
    vars,
    isPreview
  })
  const initOptionError = () => {
    setState({ optionStatus: StatusCode.error })
  }
  const initOptionLocal = ({ vars: _vars, code: _code }: { vars?: any; code?: string }) => {
    const _variables = { ...vars, ..._vars }
    !ignoreStore && optionStore?.setStatus(StatusCode.pending)
    setState({ variables: _variables, optionStatus: StatusCode.pending })
    const originOption = transform(localOption)
    const _option = injectVariable(originOption, _variables)
    setState({ option: _option, originOption: localOption, optionStatus: StatusCode.done })
    if (!ignoreStore) {
      optionStore?.setOption(_option)
      optionStore?.setStatus(StatusCode.done)
    }
    didmount && setTimeout(didmount, 20)
    return Promise.resolve(!0)
  }
  const initOption = (config: { vars?: any; code?: string }) => {
    if (isLocal) {
      return initOptionLocal(config)
    }
    const { vars: _vars, code: _code } = config
    const _variables = { ...vars, ..._vars }
    setState({ variables: _variables, optionStatus: StatusCode.pending })
    // 快速入职
    const isNeedQrCodeId = ['/entry/fast'].includes(location.pathname)
    // 快速入职
    const qr_code_id = isNeedQrCodeId ? params?.id || searchParams?.get('id') : null
    return getCommonForm({ form_type: _code || code, qr_code_id }, qr_code_id ? '/no_auth' : '')
      .then(({ success, data }: any) => {
        const dataSource = data
        if (success && data && dataSource) {
          const originOption = transform(dataSource)
          const _option = injectVariable(originOption, _variables)
          setState({
            option: _option,
            originOption: originOption,
            optionStatus: StatusCode.done
          })
          didmount && setTimeout(didmount, 20)
        } else if (success && data && !dataSource) {
          const originOption = transform(dataSource)
          const _option = injectVariable(originOption, _variables)
          setState({
            option: _option,
            originOption,
            optionStatus: StatusCode.error
          })
        } else {
          initOptionError()
        }
      })
      .catch((error: any) => {
        console.error(error)
        initOptionError()
      })
  }
  const refreshOption = (newVars: any) => {
    // console.log('newVars', newVars, stateRef.current.originOption)
    const _variables = {
      ...stateRef.current.variables,
      ...newVars
    }
    const _option = injectVariable(stateRef.current.originOption, _variables)
    setState({ option: _option, variables: _variables })
  }
  useEffect(() => {
    if (immediate && code) {
      optionStore.clear()
      stateStore.clear()
      setTimeout(() => {
        initOption({ code })
      }, 50)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return {
    option: state.option,
    stateStore,
    originOption: state.originOption,
    optionStatus: state.optionStatus,
    initOption,
    refreshOption,
    setFormOptionItem,
    getFormOptionItem
  }
}
